/**
 * Settings.
 *
 * The server keeps settings as a flat collection of records — key, description,
 * value, updatedAt, updatedBy, version — served by GET /system/settings and
 * written one key at a time by PATCH /system/settings. One key per write is the
 * right model: a whole-document PUT means two operators editing different
 * settings during an incident silently overwrite each other.
 *
 * Two things shape how this screen looks, and both are honesty rather than
 * taste.
 *
 * The first is that a setting is a *record*, not a form field. Every section
 * therefore has the same anatomy — a sentence saying what the key governs, rows
 * of one decision each, then a footer carrying the provenance (key, version,
 * who, when) beside the actions. An operator who has read one section can read
 * all of them, and the machine facts in that footer are set in mono, so a claim
 * the system is making about itself is distinguishable from prose at a glance.
 *
 * The second is that most of these documents are *stored but unread*. Nothing
 * in this build consults the autonomy policy, the routing weights or the
 * retention windows; the defaults compiled into the binary are what is in force.
 * That is stated on the section it applies to — see `enforcementOf` — because a
 * form that hides it invites an operator to set a retention window, watch it
 * save, and believe their audit trail is now governed by it. The same instinct
 * is why the sections whose keys the server has never held say so instead of
 * offering an editor over invented defaults.
 *
 * Prose is prose and state is state: the description of a section is a plain
 * lede, never a coloured banner. A [Notice] here means something happened or
 * something is wrong, so that when one appears it is worth reading.
 */

import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, request, type PageResponse } from '@/api/client'
import {
  Page,
  PageHeader,
  Panel,
  Badge,
  Button,
  EmptyState,
  KeyValue,
  StatusDot,
} from '@/components/primitives'
import {
  QueryState,
  RouteTabs,
  TextField,
  ChoiceField,
  CheckboxField,
  SelectField,
  Segmented,
  Notice,
  Dialog,
  CopyButton,
  useApiErrorMessage,
} from '@/components/controls'
import { useT, useLocale } from '@/i18n/context'
import { formatDateTime, formatPercent, formatRelative, type MessageKey } from '@/i18n'
import { useTheme } from '@/hooks/useTheme'
import { useDensity } from '@/hooks/useDensity'
import { useForeignWrite } from '@/hooks/useForeignWrite'
import type { ID, ISOTime, SystemCapabilities, SystemInfo } from '@/types/domain'
import {
  AUTONOMY_FIELDS,
  AUTONOMY_HINT,
  DECISIONS,
  DECISION_LABEL,
  EDITED_KEYS,
  KEY_AUTONOMY,
  KEY_RETENTION,
  KEY_ROUTING_WEIGHTS,
  KEY_SECURITY,
  KEY_SUPERVISOR,
  RETAIN_INDEFINITELY,
  RETENTION_FIELDS,
  ROUTING_LABEL,
  asObject,
  autonomousMinutesOf,
  changedKeys,
  chiefChanged,
  chiefDraftOf,
  chiefIsDirty,
  chiefValueOf,
  enforcementOf,
  isDirty,
  isKnownDecision,
  isNormalised,
  normalise,
  numberFieldsOf,
  parseDays,
  requiresMFAForApprovals,
  securityValueWith,
  sharesOf,
  stringFieldsOf,
  sumOf,
} from './settingsModel'
import './SettingsPage.css'

/**
 * Mirrors api.SettingResponse.
 *
 * `value` is `any` on the server and is not validated on write, so it is
 * `unknown` here. Each editor narrows the one key it owns and ignores what it
 * cannot read, rather than trusting a shape nothing enforces.
 */
interface SettingResponse {
  key: string
  description?: string
  value: unknown
  updatedAt: ISOTime
  updatedBy?: string
  version: number
}

/** Mirrors auth.Session as GET /auth/sessions returns it. The token hash and
 *  the CSRF nonce are `json:"-"` on the server and never reach the browser. */
interface AuthSession {
  id: ID
  userId: ID
  username: string
  role: string
  createdAt: ISOTime
  lastSeenAt: ISOTime
  expiresAt: ISOTime
  idleExpiresAt: ISOTime
  ip?: string
  userAgent?: string
  mfaVerified: boolean
  /** The operator ticked "keep me signed in", so this one holds the long
   *  lifetime rather than the ordinary pair. Shown, because a session that
   *  outlives every other one by weeks should be legible as a decision somebody
   *  made and not as a date nobody can explain. */
  remembered: boolean
  revokedAt?: ISOTime
  revokedReason?: string
  rotatedFrom?: ID
}

/** Mirrors api.SessionResponseBody. This page reads it for two facts the
 *  settings collection does not carry: which session is this one, and whether a
 *  second factor is enrolled. */
interface AuthSessionResponse {
  authenticated: boolean
  actor?: {
    userId: ID
    username: string
    sessionId: ID
    mfaEnrolled: boolean
    mfaVerified: boolean
  }
  csrfToken?: string
}

/** Mirrors api.TOTPEnrolmentResponse. The secret is returned exactly once. */
interface TOTPEnrolmentResponse {
  secret: string
  url: string
  digits: number
  period: number
}

/** Mirrors api.TOTPRecoveryResponse. Only the hashes are kept server-side, so
 *  these codes are shown once and never again. */
interface TOTPRecoveryResponse {
  recoveryCodes: string[]
}

/* The sections, in the order an operator meets them: whose subscription Kimene's
   own judgement is spent on, what it may do, how it chooses, what it keeps, who
   may reach it, how it looks, and finally the raw keys nothing here has an
   editor for.

   The chief is first, and so is the tab an operator lands on. It is the only one
   of the four editable keys anything in this build actually reads — the three
   under it are stored and unread, and say so — and it is the setting that
   decides whether the board's suggestions are a rules engine's guess or a
   model's judgement. Landing on a form that governs nothing, when there is one
   beside it that governs the screen they came from, is the wrong first
   impression to keep for the sake of an order nobody chose deliberately.

   There is no notifications tab. It held a warning and an empty state, which is
   a tab whose entire content is an apology; the one fact it carried — that
   notifications are derived rather than chosen — now sits on the notification
   retention window, which is the only thing about them anybody can set. */
const SECTIONS = ['chief', 'autonomy', 'routing', 'retention', 'security', 'appearance', 'other'] as const
type SectionId = (typeof SECTIONS)[number]

const SECTION_LABEL: Record<SectionId, MessageKey> = {
  chief: 'settings.chief',
  autonomy: 'settings.autonomy',
  routing: 'settings.routing',
  retention: 'settings.retention',
  security: 'settings.security',
  appearance: 'settings.appearance',
  other: 'settings.other',
}

export function SettingsPage() {
  const { section } = useParams<{ section: string }>()

  if (!section || !SECTIONS.includes(section as SectionId)) {
    return <Navigate to={`/settings/${SECTIONS[0]}`} replace />
  }
  return <SettingsBody section={section as SectionId} />
}

function SettingsBody({ section }: { section: SectionId }) {
  const t = useT()
  const settings = useSettings()
  const tabs = SECTIONS.map((s) => ({ to: `/settings/${s}`, label: t(SECTION_LABEL[s]) }))
  const held = settings.data?.items.length

  return (
    <Page
      header={
        <PageHeader
          title={t('settings.title')}
          subtitle={held != null ? t('settings.keyCount', { count: held }) : undefined}
        >
          <RouteTabs items={tabs} ariaLabel={t('settings.title')} />
        </PageHeader>
      }
    >
      <div className="k-settings">
        {section === 'chief' && <ChiefSection />}
        {section === 'autonomy' && <AutonomySection />}
        {section === 'routing' && <RoutingSection />}
        {section === 'retention' && <RetentionSection />}
        {section === 'security' && <SecuritySection />}
        {section === 'appearance' && <AppearanceSection />}
        {section === 'other' && (
          <>
            <DeploymentPanel />
            <OtherSection />
          </>
        )}
      </div>
    </Page>
  )
}

/* ------------------------------------------------------------ Shared data */

/** One query for the whole collection. The server returns every key in a single
 *  unpaginated page, so a fetch per section would be four requests for one
 *  document and four chances for the sections to disagree about it. */
function useSettings() {
  return useQuery({
    queryKey: ['system-settings'],
    queryFn: () => api.get<PageResponse<SettingResponse>>('/system/settings'),
  })
}

/** What an editor needs to write its one key, without owning the mutation —
 *  see [SettingSection] for why the two are separated. */
interface SettingSaver {
  write: (body: { key: string; description?: string; value: unknown }) => void
  busy: boolean
  error: unknown
  saved: boolean
  /** How many writes this tab has landed. Read by [SettingSection] to tell a
   *  version bump it caused from one somebody else caused — the two look
   *  identical in the refetched record and mean opposite things. */
  writes: number
}

function useSaveSetting(): SettingSaver {
  const queryClient = useQueryClient()
  const [writes, setWrites] = useState(0)
  const mutation = useMutation({
    mutationFn: (body: { key: string; description?: string; value: unknown }) =>
      api.patch<SettingResponse>('/system/settings', body),
    // The response body is deliberately not written into the cache. The handler
    // renders the setting it constructed rather than the one it stored, so its
    // `version` is 0 on every write while the stored record has already moved
    // on; refetching is the only way to hold a version the server agrees with.
    onSuccess: () => {
      setWrites((n) => n + 1)
      void queryClient.invalidateQueries({ queryKey: ['system-settings'] })
    },
  })
  return {
    write: mutation.mutate,
    busy: mutation.isPending,
    error: mutation.error,
    saved: mutation.isSuccess,
    writes,
  }
}

/* -------------------------------------------------------- Section anatomy */

/**
 * The frame every key editor shares: say what the key governs, say whether
 * anything reads it, fetch the collection, locate one key, report its absence
 * rather than inventing it.
 *
 * The mutation lives here rather than inside the editor, and the editors are
 * remounted on a version change (see the `key` at each call site) so that a
 * save — anyone's save — reseeds the form instead of leaving a stale draft that
 * would be written straight back over the top. Keeping the mutation one level
 * up is what lets the confirmation survive that remount; hold it in the editor
 * and the "saved" notice is destroyed by the refetch that proves it.
 */
function SettingSection({
  settingKey,
  title,
  lede,
  footnote,
  children,
}: {
  settingKey: string
  title: string
  lede: string
  /** A second fact about this key, shown with the enforcement warning. */
  footnote?: string
  children: (setting: SettingResponse, saver: SettingSaver) => ReactNode
}) {
  const t = useT()
  const settings = useSettings()
  const saver = useSaveSetting()
  const setting = (settings.data?.items ?? []).find((s) => s.key === settingKey)
  const unread = enforcementOf(settingKey) === 'stored-only'
  const overwritten = useForeignWrite(setting?.version ?? 0, saver.writes)

  return (
    <Panel
      title={
        <div className="k-setting__head">
          <h2 className="k-panel__title">{title}</h2>
          {unread && (
            <Badge tone="warn" subtle>
              {t('settings.storedOnly')}
            </Badge>
          )}
        </div>
      }
    >
      <div className="k-setting">
        <p className="k-setting__lede">{lede}</p>
        {unread && (
          <Notice tone="warn">
            {t('settings.storedOnlyHint')}
            {footnote && ` ${footnote}`}
          </Notice>
        )}
        <QueryState
          pending={settings.isPending}
          error={settings.error}
          onRetry={() => void settings.refetch()}
        >
          {overwritten && <Notice tone="warn">{t('settings.rewritten')}</Notice>}
          {setting ? (
            children(setting, saver)
          ) : (
            <EmptyState
              title={t('settings.missingKey', { key: settingKey })}
              hint={t('settings.missingKeyHint')}
            />
          )}
        </QueryState>
      </div>
    </Panel>
  )
}

/** One decision: what it is on the left, the control that sets it on the right.
 *  Every editable setting on this page is one of these, which is what lets six
 *  identical-looking dropdowns become six readable rows. */
function SettingRow({
  label,
  hint,
  changed,
  children,
}: {
  label: string
  hint?: string
  changed?: boolean
  children: ReactNode
}) {
  const t = useT()
  return (
    <div className="k-setting__row">
      <div className="k-setting__rowtext">
        <p className="k-setting__rowlabel">
          {label}
          {changed && <StatusDot tone="accent" label={t('settings.changed')} />}
        </p>
        {hint && <p className="k-sub">{hint}</p>}
      </div>
      <div className="k-setting__rowctl">{children}</div>
    </div>
  )
}

/**
 * Where a setting came from: its key, its version, who last wrote it and when.
 *
 * All four are facts the machine is asserting, so all four are mono. The
 * separators and the "nobody recorded" fallback are not — the difference is the
 * point of the convention, and this line is where an operator checks whether
 * the value in front of them is one a person chose or one the seeder left.
 */
function SettingProvenance({ setting }: { setting: SettingResponse }) {
  const t = useT()
  const { locale } = useLocale()
  return (
    <p className="k-setting__prov">
      <span className="k-mono">{setting.key}</span>
      <span className="k-setting__sep" aria-hidden>
        ·
      </span>
      <span className="k-mono">v{setting.version}</span>
      <span className="k-setting__sep" aria-hidden>
        ·
      </span>
      {setting.updatedBy ? (
        <span className="k-mono">{setting.updatedBy}</span>
      ) : (
        <span className="k-dash">{t('state.unknown')}</span>
      )}
      <span className="k-setting__sep" aria-hidden>
        ·
      </span>
      <span className="k-mono">{formatDateTime(setting.updatedAt, locale)}</span>
    </p>
  )
}

/**
 * The footer every editor ends with: provenance on the left, what you can do
 * about it on the right.
 *
 * The confirmation is withdrawn as soon as the form is dirty again, because
 * "saved" sitting above unsaved edits is a worse lie than no notice at all. The
 * revert appears only when there is something to revert, so the row is quiet
 * until the operator has made it loud.
 */
function SettingFoot({
  setting,
  saver,
  dirty,
  blocked,
  onSave,
  onRevert,
}: {
  setting: SettingResponse
  saver: SettingSaver
  dirty: boolean
  blocked?: boolean
  onSave: () => void
  onRevert: () => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  return (
    <>
      {saver.error != null && <Notice tone="err">{describe(saver.error).title}</Notice>}
      {saver.error == null && saver.saved && !dirty && (
        <Notice tone="ok">{t('settings.saved')}</Notice>
      )}
      <div className="k-setting__foot">
        <SettingProvenance setting={setting} />
        <div className="k-setting__acts">
          {dirty && (
            <Badge tone="warn" subtle>
              {t('settings.unsaved')}
            </Badge>
          )}
          {dirty && (
            <Button size="sm" onClick={onRevert}>
              {t('settings.revert')}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            busy={saver.busy}
            disabled={!dirty || blocked}
            onClick={onSave}
          >
            {saver.busy ? t('common.saving') : t('action.save')}
          </Button>
        </div>
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ Chief */

/** The fields the picker reads — a subset of api.AccountOverview. */
interface ChiefAccountRow {
  id: ID
  label: string
  provider: string
  enabled: boolean
  state: string
  sharedStorage?: boolean
}

/** The fields the picker reads — a subset of api.ModelResponse. */
interface ChiefModelRow {
  id: ID
  provider: string
  displayName: string
}

/**
 * Whose subscription Kimene's own judgement is spent on.
 *
 * This is the first thing an operator sets and the reason the board's
 * suggestions are worth reading: without it the chief analyses a session on
 * that session's own account, which is honest but narrow — the account doing
 * the judging is the one being judged (ADR-0025). With it, one account reads
 * every agent's last turn with the project record beside it.
 *
 * The section says what spends a request and when, because that is the fact an
 * operator is actually deciding about. A designation costs nothing by itself:
 * the board asks only when somebody presses "propose", and the portfolio turn
 * only when it is switched on below.
 */
function ChiefSection() {
  const t = useT()
  return (
    <SettingSection
      settingKey={KEY_SUPERVISOR}
      title={t('settings.chief')}
      lede={t('settings.chiefLede')}
    >
      {(setting, saver) => (
        <ChiefEditor key={`${setting.key}:${setting.version}`} setting={setting} saver={saver} />
      )}
    </SettingSection>
  )
}

function ChiefEditor({ setting, saver }: { setting: SettingResponse; saver: SettingSaver }) {
  const t = useT()
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<PageResponse<ChiefAccountRow>>('/accounts'),
  })
  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => api.get<PageResponse<ChiefModelRow>>('/models'),
  })

  const stored = chiefDraftOf(setting.value)
  const [draft, setDraft] = useState(() => stored)
  const changed = chiefChanged(draft, stored)

  const rows = accounts.data?.items ?? []
  const account = rows.find((a) => a.id === draft.accountProfileId)
  // The account fixes the provider, so only that provider's models can be
  // offered — the same rule the start dialog keeps. A model of the other
  // provider would be handed to a CLI that has never heard of it.
  const providerModels = useMemo(
    () => (models.data?.items ?? []).filter((m) => !account || m.provider === account.provider),
    [models.data, account],
  )
  // A stored designation whose account has since been deleted must still be
  // visible. Offering it back as its own option is what lets an operator see
  // that the id is dangling and choose another; dropping it would show an empty
  // field over a document that still names something.
  const dangling = draft.accountProfileId !== '' && !account

  // Switching the portfolio turn on without an account designated is a state
  // the server refuses, so the form refuses it first rather than saving a
  // document that cannot work and reporting the refusal on another screen.
  const blocked = draft.enabled && draft.accountProfileId === ''
  const loopMinutes = autonomousMinutesOf(setting.value)

  const submit = () =>
    saver.write({
      key: setting.key,
      description: setting.description,
      value: chiefValueOf(draft, setting.value),
    })

  return (
    <>
      <div className="k-setting__rows">
        <SettingRow
          label={t('settings.chiefAccount')}
          hint={t('settings.chiefAccountHint')}
          changed={changed.has('accountProfileId')}
        >
          <SelectField
            label={t('settings.chiefAccount')}
            labelHidden
            value={draft.accountProfileId}
            onChange={(v) =>
              setDraft((prev) => ({
                ...prev,
                accountProfileId: v,
                // The model belongs to the provider; changing the account
                // invalidates it rather than carrying a Codex model onto a
                // Claude subscription.
                modelId: '',
              }))
            }
            options={[
              { value: '', label: t('settings.chiefAccountNone') },
              ...(dangling
                ? [{ value: draft.accountProfileId, label: t('settings.chiefAccountGone') }]
                : []),
              ...rows.map((a) => ({
                value: a.id,
                // What is wrong with an account is on the option itself, so the
                // list explains why the one an operator expected to work does
                // not — rather than hiding it and leaving them to wonder where
                // it went.
                label:
                  `${a.label} · ${a.provider}` +
                  (a.enabled ? '' : ` · ${t('settings.chiefAccountOff')}`) +
                  (a.state && a.state !== 'READY' ? ` · ${a.state}` : ''),
              })),
            ]}
          />
        </SettingRow>

        <SettingRow
          label={t('settings.chiefModel')}
          hint={t('settings.chiefModelHint')}
          changed={changed.has('modelId')}
        >
          <SelectField
            label={t('settings.chiefModel')}
            labelHidden
            value={draft.modelId}
            disabled={!account}
            onChange={(v) => setDraft((prev) => ({ ...prev, modelId: v }))}
            options={[
              { value: '', label: t('settings.chiefModelDefault') },
              ...providerModels.map((m) => ({ value: m.id, label: m.displayName })),
            ]}
          />
        </SettingRow>

        <SettingRow
          label={t('settings.chiefPortfolio')}
          hint={t('settings.chiefPortfolioHint')}
          changed={changed.has('enabled')}
        >
          <CheckboxField
            label={t('settings.chiefPortfolioOn')}
            checked={draft.enabled}
            onChange={(v) => setDraft((prev) => ({ ...prev, enabled: v }))}
          />
        </SettingRow>
      </div>

      {blocked && <Notice tone="warn">{t('settings.chiefNeedsAccount')}</Notice>}

      {/* A timer nobody set is not mentioned. One that *is* set is, every time
          this section is opened: an operator must never have to wonder whether
          something is deciding on its own, and this screen has no field for it
          — so saying so is the only way they find out here. */}
      {loopMinutes > 0 && (
        <Notice tone="accent">{t('settings.chiefLoopOn', { minutes: loopMinutes })}</Notice>
      )}

      <SettingFoot
        setting={setting}
        saver={saver}
        dirty={chiefIsDirty(draft, stored)}
        blocked={blocked}
        onSave={submit}
        onRevert={() => setDraft(stored)}
      />
    </>
  )
}

/* --------------------------------------------------------------- Autonomy */

function AutonomySection() {
  const t = useT()
  return (
    <SettingSection
      settingKey={KEY_AUTONOMY}
      title={t('settings.autonomy')}
      lede={t('settings.autonomyLede')}
    >
      {(setting, saver) => (
        <AutonomyEditor key={`${setting.key}:${setting.version}`} setting={setting} saver={saver} />
      )}
    </SettingSection>
  )
}

function AutonomyEditor({ setting, saver }: { setting: SettingResponse; saver: SettingSaver }) {
  const t = useT()
  const stored = stringFieldsOf(setting.value)
  const [draft, setDraft] = useState<Record<string, string>>(() => stored)
  const changed = changedKeys(draft, stored)

  const submit = () =>
    saver.write({
      key: setting.key,
      description: setting.description,
      // Merged over the stored object rather than sent alone: the write
      // replaces the whole value, so a client that posted only the six fields
      // it knows would silently delete a seventh the server had added.
      value: { ...asObject(setting.value), ...draft },
    })

  return (
    <>
      <div className="k-setting__rows">
        {AUTONOMY_FIELDS.map(({ field, label }) => {
          const current = draft[field] ?? ''
          // A stored value outside the three known decisions is offered back as
          // its own option, marked UNKNOWN. Coercing it into the closest match
          // would rewrite a policy the operator never touched, on the first save
          // of any field.
          const known = isKnownDecision(current)
          return (
            <SettingRow
              key={field}
              label={t(label)}
              hint={t(AUTONOMY_HINT[field])}
              changed={changed.has(field)}
            >
              <ChoiceField
                label={t(label)}
                labelHidden
                value={current}
                onChange={(v) => setDraft((prev) => ({ ...prev, [field]: v }))}
                options={[
                  ...(known
                    ? []
                    : [
                        {
                          value: current,
                          label: current || t('settings.decisionUnknown'),
                          tone: 'unknown' as const,
                        },
                      ]),
                  ...DECISIONS.map((d) => ({ value: d, label: t(DECISION_LABEL[d]) })),
                ]}
              />
            </SettingRow>
          )
        })}
      </div>

      <SettingFoot
        setting={setting}
        saver={saver}
        dirty={isDirty(draft, stored)}
        onSave={submit}
        onRevert={() => setDraft(stored)}
      />
    </>
  )
}

/* ---------------------------------------------------------------- Routing */

function RoutingSection() {
  const t = useT()
  return (
    <SettingSection
      settingKey={KEY_ROUTING_WEIGHTS}
      title={t('settings.routing')}
      lede={t('settings.routingLede')}
      footnote={t('settings.routingInert')}
    >
      {(setting, saver) => (
        <RoutingEditor key={`${setting.key}:${setting.version}`} setting={setting} saver={saver} />
      )}
    </SettingSection>
  )
}

/**
 * The routing weights are relative importances the router normalises, so the
 * number that governs a decision is not the slider's value but its share of the
 * total: 0.20 in a set summing to 4.00 is a twentieth of the decision, not a
 * fifth. Both are on screen for that reason, the share in the column an
 * operator reads down. The running sum is always visible, and a set that is not
 * 1.00 is flagged with the rescale attached rather than silently accepted.
 */
function RoutingEditor({ setting, saver }: { setting: SettingResponse; saver: SettingSaver }) {
  const t = useT()
  const stored = numberFieldsOf(setting.value)
  const [weights, setWeights] = useState<Record<string, number>>(() => stored)

  const entries = useMemo(
    () => Object.entries(weights).sort(([a], [b]) => a.localeCompare(b)),
    [weights],
  )
  const sum = sumOf(weights)
  const shares = sharesOf(weights)
  const changed = changedKeys(weights, stored)
  const normalised = isNormalised(sum)

  const submit = () =>
    saver.write({
      key: setting.key,
      description: setting.description,
      value: { ...asObject(setting.value), ...weights },
    })

  return (
    <>
      {entries.length === 0 ? (
        <EmptyState title={t('settings.noWeights')} hint={t('settings.noWeightsHint')} />
      ) : (
        <>
          <div className="k-weights__legend" aria-hidden>
            <span className="k-label">{t('settings.weightShare')}</span>
          </div>
          <ul className="k-weights">
            {entries.map(([key, value]) => {
              const label = ROUTING_LABEL[key]
              return (
                <li className="k-weights__row" key={key}>
                  <label className="k-weights__text" htmlFor={`w-${key}`}>
                    <span className="k-weights__name">
                      {label ? t(label) : <span className="k-mono">{key}</span>}
                      {changed.has(key) && (
                        <StatusDot tone="accent" label={t('settings.changed')} />
                      )}
                    </span>
                    {/* The component's own name, in mono: it is what the stored
                        document and every recorded RoutingDecision call it, and
                        it is the string an operator greps for. */}
                    {label && <span className="k-sub k-mono">{key}</span>}
                  </label>
                  <input
                    id={`w-${key}`}
                    className="k-weights__slider"
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={value}
                    onChange={(e) =>
                      setWeights((prev) => ({ ...prev, [key]: Number(e.target.value) }))
                    }
                  />
                  <output className="k-mono k-weights__val" htmlFor={`w-${key}`}>
                    {value.toFixed(2)}
                  </output>
                  <span className="k-mono k-weights__share">
                    <span className="k-visually-hidden">{t('settings.weightShare')} </span>
                    {formatPercent(shares[key])}
                  </span>
                </li>
              )
            })}
          </ul>

          <div
            className={`k-weights__sum ${normalised ? '' : 'k-weights__sum--off'}`}
            role="status"
          >
            <span className="k-label">{t('settings.weightSum')}</span>
            <span className="k-mono k-weights__sumval">{sum.toFixed(2)}</span>
            {!normalised && (
              <>
                <span className="k-sub">{t('settings.weightShareHint')}</span>
                <Button
                  size="sm"
                  onClick={() => setWeights(normalise(weights))}
                  disabled={sum <= 0}
                >
                  {t('settings.normalize')}
                </Button>
              </>
            )}
          </div>
        </>
      )}

      <SettingFoot
        setting={setting}
        saver={saver}
        dirty={isDirty(weights, stored)}
        blocked={entries.length === 0}
        onSave={submit}
        onRevert={() => setWeights(stored)}
      />
    </>
  )
}

/* -------------------------------------------------------------- Retention */

function RetentionSection() {
  const t = useT()
  return (
    <SettingSection
      settingKey={KEY_RETENTION}
      title={t('settings.retention')}
      lede={t('settings.retentionLede')}
    >
      {(setting, saver) => (
        <RetentionEditor
          key={`${setting.key}:${setting.version}`}
          setting={setting}
          saver={saver}
        />
      )}
    </SettingSection>
  )
}

/**
 * The retention windows.
 *
 * "Never expires" is a checkbox rather than a number an operator has to know to
 * type. The stored value is still -1 and the field says so, because the
 * document is what somebody will read in a backup — but -1 as the *interface*
 * for "keep this forever" is a command-line idiom wearing a form's clothes, and
 * the one field it protects is the one where a mistyped 1 is unrecoverable.
 */
function RetentionEditor({ setting, saver }: { setting: SettingResponse; saver: SettingSaver }) {
  const t = useT()

  const stored = numberFieldsOf(setting.value)
  const storedText: Record<string, string> = {}
  for (const { field } of RETENTION_FIELDS) storedText[field] = String(stored[field] ?? '')

  // Held as text, not as numbers: a field that reformats "-" into 0 while the
  // operator is still typing makes a negative value practically untypable, and
  // an empty box has to stay empty long enough to be typed into.
  const [draft, setDraft] = useState<Record<string, string>>(() => storedText)
  // What to put back when "never expires" is unticked. Remembered rather than
  // guessed: any default this page invented would be a policy nobody chose.
  const lastFinite = useRef<Record<string, string>>({})

  const parsed = RETENTION_FIELDS.map(
    ({ field }) => [field, parseDays(draft[field] ?? '')] as const,
  )
  const invalid = parsed.some(([, days]) => days === null)
  const changed = changedKeys(draft, storedText)

  const setField = (field: string, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }))

  const submit = () => {
    const value: Record<string, unknown> = { ...asObject(setting.value) }
    for (const [field, days] of parsed) if (days !== null) value[field] = days
    saver.write({ key: setting.key, description: setting.description, value })
  }

  return (
    <>
      <div className="k-setting__rows">
        {RETENTION_FIELDS.map(({ field, label, hint }) => {
          const raw = draft[field] ?? ''
          const days = parseDays(raw)
          const forever = days === RETAIN_INDEFINITELY
          return (
            <SettingRow key={field} label={t(label)} hint={t(hint)} changed={changed.has(field)}>
              <div className="k-setting__days">
                <TextField
                  label={t(label)}
                  labelHidden
                  value={forever ? '' : raw}
                  onChange={(v) => setField(field, v)}
                  inputMode="numeric"
                  mono
                  disabled={forever}
                  hint={
                    forever
                      ? t('settings.retentionStores', { value: RETAIN_INDEFINITELY })
                      : t('settings.days')
                  }
                  error={days === null ? t('settings.retentionInvalid') : undefined}
                />
                <CheckboxField
                  label={t('settings.retentionKeepForever')}
                  checked={forever}
                  onChange={(on) => {
                    if (on) {
                      lastFinite.current[field] = raw
                      setField(field, String(RETAIN_INDEFINITELY))
                    } else {
                      setField(field, lastFinite.current[field] ?? '')
                    }
                  }}
                />
              </div>
            </SettingRow>
          )
        })}

        {/* The audit trail, stated rather than offered.
         *
         * It had a box here until 2026-10-17, and the box did nothing: no
         * sweeper has ever read `auditDays`, so an operator who narrowed it —
         * this deployment's did, to 30 days, past a warning saying records
         * outside the window could not be brought back — had bought consent for
         * a deletion that never happened. Removing the box without saying
         * anything would leave a retention screen that is silent about the
         * largest class of history Kimene keeps, which reads as an oversight;
         * the fact is what the control was obscuring, so the fact is what
         * stands here. */}
        <SettingRow label={t('settings.retentionAudit')} hint={t('settings.retentionAuditHint')}>
          <p className="k-setting__fixed">{t('settings.retentionAuditNever')}</p>
        </SettingRow>
      </div>

      <SettingFoot
        setting={setting}
        saver={saver}
        dirty={isDirty(draft, storedText)}
        blocked={invalid}
        onSave={submit}
        onRevert={() => setDraft(storedText)}
      />
    </>
  )
}

/* --------------------------------------------------------------- Security */

function SecuritySection() {
  return (
    <>
      <TotpPanel />
      <ApprovalMFAPanel />
      <SessionsPanel />
    </>
  )
}

/**
 * The approval second factor (ADR-0032).
 *
 * It is here rather than beside the other stored policies because it is not a
 * preference: it decides whether a stolen session cookie can sign off a
 * production deploy, and the only other control that matters to that question —
 * enrolment — is the panel directly above it.
 *
 * It had no control at all until 2026-10-17. The gate was written, the write
 * path was guarded, the refusal had its own error code and a test covered each
 * of them, and nothing in the console could turn it on: the one way to engage
 * it was a hand-written PATCH. Built is not engaged, and a security control
 * nobody can reach is off.
 */
function ApprovalMFAPanel() {
  const t = useT()
  const session = useAuthSession()
  return (
    <SettingSection
      settingKey={KEY_SECURITY}
      title={t('settings.approvalMfa')}
      lede={t('settings.approvalMfaLede')}
    >
      {(setting, saver) => (
        <ApprovalMFAEditor
          key={`${setting.key}:${setting.version}`}
          setting={setting}
          saver={saver}
          enrolled={session.data?.actor?.mfaEnrolled ?? false}
          verified={session.data?.actor?.mfaVerified ?? false}
        />
      )}
    </SettingSection>
  )
}

/**
 * One checkbox, and the two reasons the server would refuse it.
 *
 * Turning it *on* from a session that did not present a second factor is
 * refused by `refuseUnsatisfiableMFAPolicy` with a 409, because it would leave
 * nobody able to decide a high-risk approval — the one action no other account
 * can perform on an operator's behalf. That refusal is mirrored here rather
 * than left to the round trip: an operator should learn why before they press,
 * and the server's sentence is the backstop, not the explanation.
 *
 * Turning it *off* is never blocked, exactly as on the server. An escape hatch
 * that needs the thing it escapes is not one.
 */
function ApprovalMFAEditor({
  setting,
  saver,
  enrolled,
  verified,
}: {
  setting: SettingResponse
  saver: SettingSaver
  enrolled: boolean
  verified: boolean
}) {
  const t = useT()
  const stored = requiresMFAForApprovals(setting.value)
  const [draft, setDraft] = useState(stored)

  const dirty = draft !== stored
  const blocked = draft && !stored && !verified

  return (
    <>
      <div className="k-setting__rows">
        <SettingRow
          label={t('settings.approvalMfaRequire')}
          hint={t('settings.approvalMfaRequireHint')}
          changed={dirty}
        >
          <CheckboxField
            label={t('settings.approvalMfaRequireOn')}
            checked={draft}
            onChange={setDraft}
          />
        </SettingRow>
      </div>

      {blocked && (
        <Notice tone="warn">
          {enrolled ? t('settings.approvalMfaNeedsSignIn') : t('settings.approvalMfaNeedsEnrolment')}
        </Notice>
      )}

      <SettingFoot
        setting={setting}
        saver={saver}
        dirty={dirty}
        blocked={blocked}
        onSave={() =>
          saver.write({
            key: setting.key,
            description: setting.description,
            value: securityValueWith(setting.value, draft),
          })
        }
        onRevert={() => setDraft(stored)}
      />
    </>
  )
}

function useAuthSession() {
  return useQuery({
    queryKey: ['auth-session'],
    queryFn: () => api.get<AuthSessionResponse>('/auth/session'),
  })
}

/**
 * The second factor.
 *
 * Enrolment is a three-step exchange with the server — begin, confirm with a
 * working code, record the recovery codes — and it is rendered as one, because
 * a single checkbox could not have worked: there is nothing to toggle until a
 * code has proved the authenticator app and the server agree on the secret.
 */
function TotpPanel() {
  const t = useT()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()
  const session = useAuthSession()
  const [enrolling, setEnrolling] = useState(false)
  const [disabling, setDisabling] = useState(false)

  const enrolled = session.data?.actor?.mfaEnrolled ?? false
  const verified = session.data?.actor?.mfaVerified ?? false

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['auth-session'] })

  return (
    <Panel
      title={t('settings.totp')}
      actions={
        session.data &&
        (enrolled ? (
          <Button variant="danger" size="sm" onClick={() => setDisabling(true)}>
            {t('settings.totpDisable')}
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => setEnrolling(true)}>
            {t('settings.totpEnrol')}
          </Button>
        ))
      }
    >
      <div className="k-setting">
        <p className="k-setting__lede">{t('settings.totpHint')}</p>
        <QueryState
          pending={session.isPending}
          error={session.error}
          onRetry={() => void session.refetch()}
        >
          <div className="k-setting__totp">
            <Badge tone={enrolled ? 'ok' : 'warn'}>
              {enrolled ? t('settings.totpOn') : t('settings.totpOff')}
            </Badge>
            {enrolled && (
              <Badge tone={verified ? 'ok' : 'neutral'} subtle>
                {verified ? t('settings.totpVerified') : t('settings.totpNotVerified')}
              </Badge>
            )}
          </div>
        </QueryState>
      </div>

      {enrolling && (
        <TotpEnrolDialog
          onClose={() => setEnrolling(false)}
          onDone={() => {
            setEnrolling(false)
            refresh()
          }}
          describe={describe}
        />
      )}
      {disabling && (
        <TotpDisableDialog
          onClose={() => setDisabling(false)}
          onDone={() => {
            setDisabling(false)
            refresh()
          }}
          describe={describe}
        />
      )}
    </Panel>
  )
}

type Describe = (err: unknown) => { title: string; hint: string }

function TotpEnrolDialog({
  onClose,
  onDone,
  describe,
}: {
  onClose: () => void
  onDone: () => void
  describe: Describe
}) {
  const t = useT()
  const [code, setCode] = useState('')

  // Started on mount rather than behind another button: the dialog exists
  // because the operator already asked to enrol, and a secret that is only
  // minted after a second click leaves an empty modal explaining nothing.
  const begin = useQuery({
    queryKey: ['totp-enrolment'],
    queryFn: () => api.post<TOTPEnrolmentResponse>('/auth/totp'),
    // The secret is issued once per call and is not idempotent, so it must not
    // be refetched behind the operator's back while they are typing a code
    // derived from the one they were shown.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const activate = useMutation({
    mutationFn: () => api.put<TOTPRecoveryResponse>('/auth/totp', { code: code.trim() }),
  })

  const recovery = activate.data?.recoveryCodes ?? []

  if (recovery.length > 0) {
    return (
      <Dialog
        title={t('settings.totpRecovery')}
        onClose={onDone}
        footer={
          <Button variant="primary" onClick={onDone}>
            {t('action.close')}
          </Button>
        }
      >
        <Notice tone="warn">{t('settings.totpRecoveryHint')}</Notice>
        <pre className="k-code" tabIndex={0}>
          {recovery.join('\n')}
        </pre>
        <CopyButton value={recovery.join('\n')} ariaLabel={t('common.copy')} />
      </Dialog>
    )
  }

  return (
    <Dialog
      title={t('settings.totpEnrol')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('action.cancel')}</Button>
          <Button
            variant="primary"
            busy={activate.isPending}
            disabled={code.trim().length === 0 || begin.data == null}
            onClick={() => activate.mutate()}
          >
            {t('settings.totpActivate')}
          </Button>
        </>
      }
    >
      <QueryState
        pending={begin.isPending}
        error={begin.error}
        onRetry={() => void begin.refetch()}
      >
        {begin.data && (
          <>
            <Notice tone="info">{t('settings.totpEnrolHint')}</Notice>
            <p className="k-label">{t('settings.totpSecret')}</p>
            <p className="k-inline">
              <span className="k-mono">{begin.data.secret}</span>
              <CopyButton value={begin.data.secret} ariaLabel={t('common.copy')} />
            </p>
            <p className="k-label">{t('settings.totpUrl')}</p>
            <p className="k-inline">
              {/* Shown as text, never as a link. An otpauth: URL carries the
                  secret in the query string, and an anchor would put it into
                  the browser's history and into any handler registered for the
                  scheme. */}
              <span className="k-mono k-sub">{begin.data.url}</span>
              <CopyButton value={begin.data.url} ariaLabel={t('common.copy')} />
            </p>
            <p className="k-sub">
              {t('settings.totpParams', {
                digits: begin.data.digits,
                period: begin.data.period,
              })}
            </p>
            <TextField
              label={t('settings.totpCode')}
              value={code}
              onChange={setCode}
              inputMode="numeric"
              autoComplete="one-time-code"
              mono
            />
            {activate.error && <Notice tone="err">{describe(activate.error).title}</Notice>}
          </>
        )}
      </QueryState>
    </Dialog>
  )
}

function TotpDisableDialog({
  onClose,
  onDone,
  describe,
}: {
  onClose: () => void
  onDone: () => void
  describe: Describe
}) {
  const t = useT()
  const [password, setPassword] = useState('')

  // `api.del` sends no body, and the server requires the password when an actor
  // removes their own factor — so this goes through `request` directly rather
  // than widening the shared client for one call site.
  const disable = useMutation({
    mutationFn: () => request<void>('/auth/totp', { method: 'DELETE', body: { password } }),
    onSuccess: onDone,
  })

  return (
    <Dialog
      title={t('settings.totpDisable')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('action.cancel')}</Button>
          <Button
            variant="danger"
            busy={disable.isPending}
            disabled={password.length === 0}
            onClick={() => disable.mutate()}
          >
            {t('settings.totpDisable')}
          </Button>
        </>
      }
    >
      <Notice tone="warn">{t('settings.totpDisableHint')}</Notice>
      <TextField
        label={t('auth.password')}
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
      />
      {disable.error && <Notice tone="err">{describe(disable.error).title}</Notice>}
    </Dialog>
  )
}

/**
 * Open browser sessions.
 *
 * They come from GET /auth/sessions. No revocation is offered until the current
 * session is known: with `currentId` still undefined every row looks like
 * somebody else's, and "revoke every other session" would end the one the
 * operator is using.
 */
function SessionsPanel() {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const describe = useApiErrorMessage()

  const auth = useAuthSession()
  const sessions = useQuery({
    queryKey: ['auth-sessions'],
    queryFn: () => api.get<PageResponse<AuthSession>>('/auth/sessions'),
  })

  const currentId = auth.data?.actor?.sessionId
  const items = sessions.data?.items ?? []
  const others = currentId ? items.filter((s) => s.id !== currentId) : []

  const revoke = useMutation({
    mutationFn: (ids: ID[]) =>
      // Sequentially, and one DELETE per session: there is no bulk revocation
      // endpoint. Doing it in parallel would fire an unbounded fan-out from a
      // single click, and a partial failure would be impossible to describe.
      ids.reduce<Promise<void>>(
        (chain, id) => chain.then(() => api.del<void>(`/auth/sessions/${id}`)),
        Promise.resolve(),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['auth-sessions'] }),
  })

  return (
    <Panel
      title={t('settings.sessions')}
      actions={
        others.length > 0 && (
          <Button
            variant="danger"
            size="sm"
            busy={revoke.isPending}
            onClick={() => revoke.mutate(others.map((s) => s.id))}
          >
            {t('settings.revokeOthers')}
          </Button>
        )
      }
      flush
    >
      {revoke.error && <Notice tone="err">{describe(revoke.error).title}</Notice>}
      <QueryState
        pending={sessions.isPending}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        empty={items.length === 0}
        emptyTitle={t('state.empty')}
      >
        <div className="k-tablewrap">
          <table className="k-table k-table--responsive">
            <caption className="k-visually-hidden">{t('settings.sessions')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('audit.client')}</th>
                <th scope="col">{t('common.created')}</th>
                <th scope="col">{t('settings.lastActive')}</th>
                <th scope="col">{t('settings.sessionExpires')}</th>
                <th scope="col">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const current = s.id === currentId
                return (
                  <tr key={s.id}>
                    <td className="k-td--strong" data-label={t('audit.client')}>
                      <span className="k-mono">{s.ip ?? '—'}</span>
                      {s.userAgent && <span className="k-sub">{s.userAgent}</span>}
                      {current && (
                        <Badge tone="accent" subtle>
                          {t('settings.currentSession')}
                        </Badge>
                      )}
                      {s.remembered && !s.revokedAt && (
                        <Badge tone="warn" subtle>
                          {t('settings.sessionRemembered')}
                        </Badge>
                      )}
                      {s.revokedAt && (
                        <Badge tone="neutral" subtle>
                          {t('settings.sessionRevoked')}
                        </Badge>
                      )}
                    </td>
                    <td data-label={t('common.created')}>{formatDateTime(s.createdAt, locale)}</td>
                    <td data-label={t('settings.lastActive')}>
                      {formatRelative(s.lastSeenAt, locale)}
                    </td>
                    <td data-label={t('settings.sessionExpires')}>
                      {formatRelative(s.expiresAt, locale)}
                      <span className="k-sub">
                        {t('settings.sessionIdle', {
                          when: formatRelative(s.idleExpiresAt, locale),
                        })}
                      </span>
                    </td>
                    <td data-label={t('common.actions')}>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={currentId == null || current || s.revokedAt != null}
                        busy={revoke.isPending}
                        onClick={() => revoke.mutate([s.id])}
                      >
                        {t('settings.revoke')}
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Panel>
  )
}

/* ------------------------------------------------------------- Appearance */

/**
 * Browser preferences.
 *
 * Nothing here is sent anywhere: theme, density and language are properties of
 * this browser and the server has no field for any of them. They therefore get
 * the one control in the console that means "this takes effect as you choose
 * it" — the segmented switch — and no Save button, because there is nothing to
 * save. Saying so on the panel is the difference between "this is local" and
 * "this does not work on my other machine and I do not know why".
 */
function AppearanceSection() {
  const t = useT()
  const { locale, setLocale } = useLocale()
  // Shared with the top bar's toggle so the two never disagree.
  const [theme, applyTheme] = useTheme()
  // Shared with the top bar's switch, the same way the theme is. It was a local
  // useState seeded from the attribute, which was true while this panel was the
  // only way to set it; the moment the top bar grew a control, a snapshot taken
  // at mount would have shown the operator the density they *had* — and gone on
  // showing it while the screen said otherwise.
  const [density, applyDensity] = useDensity()

  /**
   * The terminal's type size.
   *
   * Three steps rather than a slider: the readability floor is derived from this,
   * so every value here has to be one where 80 columns still fit on a real screen
   * — and a continuous control invites the value that makes the wall unusable.
   */
  const [termFont, setTermFont] = useState<string>(
    () => localStorage.getItem('kimene.termFont') ?? '12.5',
  )

  const applyTermFont = (next: string) => {
    document.documentElement.style.setProperty('--k-term-font', next)
    localStorage.setItem('kimene.termFont', next)
    setTermFont(next)
  }

  return (
    <Panel title={t('settings.appearance')}>
      <div className="k-setting">
        <p className="k-setting__lede">{t('settings.appearanceLocal')}</p>
        <div className="k-setting__rows">
          <SettingRow label={t('topbar.theme')}>
            <Segmented
              value={theme}
              ariaLabel={t('topbar.theme')}
              onChange={(v) => applyTheme(v === 'light' ? 'light' : 'dark')}
              options={[
                { value: 'dark', label: t('settings.themeDark') },
                { value: 'light', label: t('settings.themeLight') },
              ]}
            />
          </SettingRow>
          <SettingRow label={t('settings.density')} hint={t('settings.densityHint')}>
            <Segmented
              value={density}
              ariaLabel={t('settings.density')}
              onChange={applyDensity}
              options={[
                { value: 'tight', label: t('settings.densityTight') },
                { value: 'compact', label: t('settings.densityCompact') },
                { value: 'comfortable', label: t('settings.densityComfortable') },
              ]}
            />
          </SettingRow>
          <SettingRow label={t('settings.termFont')} hint={t('settings.termFontHint')}>
            <Segmented
              value={termFont}
              ariaLabel={t('settings.termFont')}
              onChange={applyTermFont}
              options={[
                { value: '11.5', label: t('settings.termFontSmall') },
                { value: '12.5', label: t('settings.termFontNormal') },
                { value: '14.5', label: t('settings.termFontLarge') },
              ]}
            />
          </SettingRow>
          <SettingRow label={t('topbar.language')}>
            <Segmented
              value={locale}
              ariaLabel={t('topbar.language')}
              onChange={(v) => setLocale(v === 'en' ? 'en' : 'tr')}
              options={[
                { value: 'tr', label: 'Türkçe' },
                { value: 'en', label: 'English' },
              ]}
            />
          </SettingRow>
        </div>
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ Other */

/**
 * Every key without a dedicated editor, read-only.
 *
 * The collection is open — `setup` is written by the setup handler and more will
 * follow — and a page that showed only the keys it happens to know about would
 * leave an operator unable to see a setting that is nonetheless governing their
 * install. Read-only rather than a JSON textarea: the server does not validate
 * what it stores, so a free-text editor here would be a way to write a value
 * nothing can read back.
 *
 * The server's own description is shown, attributed. It is the one piece of
 * English prose this page does not translate, because for a key nobody here has
 * heard of, the server's sentence is the only description that exists — and
 * labelling it as the server's is what stops it reading as Kimene's own
 * untranslated copy.
 */
/**
 * What the server reports about itself.
 *
 * `/system` carries the build, the environment and five capability flags, and
 * until now nothing in the console fetched it — so an operator had no way to
 * see which build was running or what it was wired for, and the one flag that
 * changes a runbook was invisible.
 *
 * That flag is `transactionalOutbox`. On a standalone datastore there are no
 * transactions, so an event is not published atomically with the write that
 * caused it: the outbox republishes pending records and delivery is
 * at-least-once. Recovering from a stuck queue is a different procedure in each
 * case, which makes this operational information rather than trivia — and it is
 * stated in words, not left as a flag the operator has to interpret.
 *
 * It lives in Settings rather than on the cockpit because none of it ever
 * changes while the process runs. A permanent sentence in the workspace an
 * operator watches all day is a permanent tax on the rows they are reading.
 */
function DeploymentPanel() {
  const t = useT()
  const { locale } = useLocale()
  const system = useQuery({
    queryKey: ['system'],
    queryFn: () => api.get<SystemInfo>('/system'),
  })
  const d = system.data

  const caps: Array<{ key: keyof SystemCapabilities; label: MessageKey }> = [
    { key: 'realtimeEvents', label: 'system.cap.realtimeEvents' },
    { key: 'terminalGateway', label: 'system.cap.terminalGateway' },
    { key: 'idempotentWrite', label: 'system.cap.idempotentWrite' },
    { key: 'transactionalOutbox', label: 'system.cap.transactionalOutbox' },
    { key: 'webBundle', label: 'system.cap.webBundle' },
  ]

  return (
    <Panel title={t('system.deployment')}>
      <div className="k-setting">
        <p className="k-setting__lede">{t('system.deploymentHint')}</p>
        <QueryState
          pending={system.isPending}
          error={system.error}
          onRetry={() => void system.refetch()}
        >
          {d && (
            <>
              <KeyValue
                items={[
                  { key: 'version', label: t('system.buildVersion'), value: d.version, mono: true },
                  ...(d.commit
                    ? [
                        {
                          key: 'commit',
                          label: t('system.buildCommit'),
                          value: d.commit,
                          mono: true,
                        },
                      ]
                    : []),
                  {
                    key: 'environment',
                    label: t('system.environment'),
                    value: d.environment,
                    mono: true,
                  },
                  { key: 'tz', label: t('system.timezone'), value: d.timezone, mono: true },
                  {
                    key: 'now',
                    label: t('system.serverTime'),
                    value: formatDateTime(d.serverTime, locale),
                    mono: true,
                  },
                ]}
              />

              <p className="k-label k-deploy__caplabel">{t('system.capabilities')}</p>
              <ul className="k-deploy__caps">
                {caps.map((c) => {
                  const on = d.capabilities[c.key]
                  return (
                    <li className="k-deploy__cap" key={c.key}>
                      {/* A word beside the dot, never the dot alone: which way a
                          wiring flag points is not something to read off a
                          colour. */}
                      <StatusDot tone={on ? 'ok' : 'neutral'} label={t(c.label)} />
                      <span className="k-deploy__capname">{t(c.label)}</span>
                      <Badge tone={on ? 'ok' : 'neutral'} subtle>
                        {on ? t('system.cap.on') : t('system.cap.off')}
                      </Badge>
                    </li>
                  )
                })}
              </ul>

              {/* Only when it is false. A note shown in both states is not
                  information — and on a replica set there is nothing here an
                  operator has to do differently. */}
              {!d.capabilities.transactionalOutbox && (
                <p className="k-setting__lede k-deploy__outbox">{t('system.outboxIdempotent')}</p>
              )}
            </>
          )}
        </QueryState>
      </div>
    </Panel>
  )
}

function OtherSection() {
  const t = useT()
  const { locale } = useLocale()
  const settings = useSettings()
  const items = (settings.data?.items ?? []).filter((s) => !EDITED_KEYS.includes(s.key))

  return (
    <Panel title={t('settings.other')}>
      <div className="k-setting">
        <p className="k-setting__lede">{t('settings.otherHint')}</p>
        <QueryState
          pending={settings.isPending}
          error={settings.error}
          onRetry={() => void settings.refetch()}
          empty={items.length === 0}
          emptyTitle={t('settings.noOtherKeys')}
          emptyHint={t('settings.noOtherKeysHint')}
        >
          <ul className="k-others">
            {items.map((s) => {
              const json = JSON.stringify(s.value, null, 2)
              return (
                <li className="k-others__item" key={s.key}>
                  <div className="k-others__head">
                    <span className="k-mono k-others__key">{s.key}</span>
                    <Badge tone="neutral" subtle>
                      {t('settings.readOnly')}
                    </Badge>
                    {enforcementOf(s.key) === 'stored-only' && (
                      <Badge tone="warn" subtle>
                        {t('settings.storedOnly')}
                      </Badge>
                    )}
                    <CopyButton value={json} ariaLabel={t('settings.copyValue')} />
                  </div>
                  {s.description && (
                    <p className="k-sub">
                      <span className="k-label">{t('settings.serverText')}</span> {s.description}
                    </p>
                  )}
                  <pre className="k-code" tabIndex={0}>
                    {json}
                  </pre>
                  <p className="k-setting__prov">
                    <span className="k-mono">v{s.version}</span>
                    <span className="k-setting__sep" aria-hidden>
                      ·
                    </span>
                    {s.updatedBy ? (
                      <span className="k-mono">{s.updatedBy}</span>
                    ) : (
                      <span className="k-dash">{t('state.unknown')}</span>
                    )}
                    <span className="k-setting__sep" aria-hidden>
                      ·
                    </span>
                    <span className="k-mono">{formatDateTime(s.updatedAt, locale)}</span>
                  </p>
                </li>
              )
            })}
          </ul>
        </QueryState>
      </div>
    </Panel>
  )
}
